import * as L from 'leaflet'
import {
  GOOGLE_MAP_TILES_ORIGIN,
  GOOGLE_MAP_TILES_REFRESH_MARGIN_MS,
  getSharedGoogleMapTilesSessionStore,
  type GoogleMapTilesSession,
  type GoogleMapTilesSessionRequest,
  type GoogleMapTilesSessionStore,
} from './google-map-tiles-session.js'
import { GOOGLE_MAPS_LOGO_SVG } from './google-maps-logo.js'

/**
 * Imagery only. 2x + highDpi tiles are 512px, drawn on a 512px / `zoomOffset: -1` grid (as the MapTiler
 * street layers): a quarter of the billable tiles of a 256px grid.
 */
export const GOOGLE_SATELLITE_SESSION_REQUEST: GoogleMapTilesSessionRequest = {
  mapType: 'satellite',
  language: 'en-US',
  region: 'US',
  scale: 'scaleFactor2x',
  highDpi: true,
}

export const GOOGLE_SATELLITE_TILE_OPTIONS = {
  maxZoom: 20,
  tileSize: 512,
  zoomOffset: -1,
  minZoom: 1,
} as const

/** Unofficial, unkeyed Google imagery; only for environments without a Map Tiles key (local dev). */
const NO_KEY_SATELLITE_URL = 'https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'

const EMPTY_TILE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
const VIEWPORT_DEBOUNCE_MS = 300

export type GoogleSatelliteLayerOptions = {
  /** Map Tiles API key. Without one the layer serves unofficial Google imagery (local dev only). */
  apiKey?: string | null
  /** Testing hook; defaults to the page-wide store for the key. */
  sessionStore?: GoogleMapTilesSessionStore
  fetch?: typeof fetch
}

export function googleSatelliteTileUrl(
  apiKey: string,
  token: string,
  coords: { x: number; y: number; z: number },
): string {
  return `${GOOGLE_MAP_TILES_ORIGIN}/v1/2dtiles/${coords.z}/${coords.x}/${coords.y}?session=${encodeURIComponent(token)}&key=${encodeURIComponent(apiKey)}`
}

/** Viewport-info query for the attribution string; bounds are clamped to what the API accepts. */
export function googleViewportInfoUrl(
  apiKey: string,
  token: string,
  zoom: number,
  bounds: { north: number; south: number; east: number; west: number },
): string {
  const clampLat = (v: number) => Math.max(-85, Math.min(85, v))
  const span = bounds.east - bounds.west
  const west = span >= 360 ? -180 : L.Util.wrapNum(bounds.west, [-180, 180], true)
  const east = span >= 360 ? 180 : L.Util.wrapNum(bounds.east, [-180, 180], true)
  const params = new URLSearchParams({
    session: token,
    key: apiKey,
    zoom: String(Math.max(0, Math.min(22, Math.round(zoom)))),
    north: String(clampLat(bounds.north)),
    south: String(clampLat(bounds.south)),
    east: String(east),
    west: String(west),
  })
  return `${GOOGLE_MAP_TILES_ORIGIN}/tile/v1/viewport?${params}`
}

/**
 * Google logo (bottom-left) and imagery copyright (bottom-right), which Google requires whenever its
 * tiles are on screen. https://developers.google.com/maps/documentation/tile/policies
 */
class GoogleAttribution {
  private logo: L.Control
  private copyright: L.Control
  private copyrightEl: HTMLElement | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private abort: AbortController | null = null

  constructor(
    private map: L.Map,
    private viewportUrl: () => string | null,
    private fetchImpl: typeof fetch,
  ) {
    const Logo = L.Control.extend({
      onAdd() {
        const el = L.DomUtil.create('div', 'gpx-google-maps-logo')
        el.style.margin = '0 0 5px 10px'
        el.style.lineHeight = '0'
        el.setAttribute('role', 'img')
        el.setAttribute('aria-label', 'Google Maps')
        el.innerHTML = GOOGLE_MAPS_LOGO_SVG
        const svg = el.querySelector('svg')
        svg?.setAttribute('height', '18')
        svg?.removeAttribute('width')
        L.DomEvent.disableClickPropagation(el)
        return el
      },
    })
    const Copyright = L.Control.extend({
      onAdd: () => {
        const el = L.DomUtil.create('div', 'gpx-google-maps-copyright')
        el.style.margin = '0'
        el.style.padding = '0 5px'
        el.style.font = '10px/16px Roboto, Arial, sans-serif'
        el.style.color = '#444'
        el.style.background = 'rgba(255, 255, 255, 0.7)'
        el.style.whiteSpace = 'nowrap'
        el.style.maxWidth = '70vw'
        el.style.overflow = 'hidden'
        el.style.textOverflow = 'ellipsis'
        L.DomEvent.disableClickPropagation(el)
        this.copyrightEl = el
        return el
      },
    })
    this.logo = new Logo({ position: 'bottomleft' })
    this.copyright = new Copyright({ position: 'bottomright' })
  }

  add() {
    this.logo.addTo(this.map)
    this.copyright.addTo(this.map)
    this.map.on('moveend', this.schedule, this)
    this.schedule()
  }

  remove() {
    this.map.off('moveend', this.schedule, this)
    if (this.timer) clearTimeout(this.timer)
    this.abort?.abort()
    this.logo.remove()
    this.copyright.remove()
    this.copyrightEl = null
  }

  schedule() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.refresh(), VIEWPORT_DEBOUNCE_MS)
  }

  private async refresh() {
    const url = this.viewportUrl()
    if (!url) return
    this.abort?.abort()
    const abort = new AbortController()
    this.abort = abort
    try {
      const res = await this.fetchImpl(url, { signal: abort.signal })
      if (!res.ok) return
      const body = (await res.json()) as { copyright?: string }
      if (this.copyrightEl && !abort.signal.aborted) {
        this.copyrightEl.textContent = body.copyright ?? ''
        this.copyrightEl.title = body.copyright ?? ''
      }
    } catch {
      // Aborted or offline; the next move retries.
    }
  }
}

export class GoogleSatelliteTileLayer extends L.TileLayer {
  private session: GoogleMapTilesSession | null = null
  private attribution: GoogleAttribution | null = null

  constructor(
    private apiKey: string,
    private store: GoogleMapTilesSessionStore,
    private fetchImpl: typeof fetch,
  ) {
    super('', { ...GOOGLE_SATELLITE_TILE_OPTIONS })
    this.session = store.current()
  }

  getTileUrl(coords: L.Coords): string {
    const session = this.store.current()
    this.session = session
    if (!session || session.expiresAt - Date.now() < GOOGLE_MAP_TILES_REFRESH_MARGIN_MS) {
      this.requestSession(session === null)
    }
    if (!session) return EMPTY_TILE
    return googleSatelliteTileUrl(this.apiKey, session.token, {
      x: coords.x,
      y: coords.y,
      z: this._getZoomForUrl(),
    })
  }

  onAdd(map: L.Map): this {
    this.attribution = new GoogleAttribution(map, () => this.viewportUrl(), this.fetchImpl)
    this.attribution.add()
    super.onAdd(map)
    return this
  }

  onRemove(map: L.Map): this {
    this.attribution?.remove()
    this.attribution = null
    return super.onRemove(map)
  }

  private pending: Promise<unknown> | null = null

  /** Creates or refreshes the session; with `redraw`, reloads the placeholder tiles once it lands. */
  private requestSession(redraw: boolean) {
    if (this.pending) return
    this.pending = this.store
      .ensure()
      .then((session) => {
        this.session = session
        if (redraw && this._map) {
          this.redraw()
          this.attribution?.schedule()
        }
      })
      .catch((error: unknown) => {
        console.warn('Google satellite tiles unavailable', error)
      })
      .finally(() => {
        this.pending = null
      })
  }

  private viewportUrl(): string | null {
    const map = this._map
    if (!map || !this.session) return null
    const b = map.getBounds()
    return googleViewportInfoUrl(this.apiKey, this.session.token, map.getZoom() + GOOGLE_SATELLITE_TILE_OPTIONS.zoomOffset, {
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    })
  }
}

/**
 * Leaflet satellite basemap from the Google Map Tiles API. Creates (or reuses) the page-wide session on
 * first add, so maps that never show satellite never call Google.
 */
export function createGoogleSatelliteLayer(options: GoogleSatelliteLayerOptions): L.TileLayer {
  const apiKey = options.apiKey?.trim()
  if (!apiKey) {
    return L.tileLayer(NO_KEY_SATELLITE_URL, { subdomains: ['mt0', 'mt1', 'mt2', 'mt3'], maxZoom: 20 })
  }
  const store =
    options.sessionStore ?? getSharedGoogleMapTilesSessionStore(apiKey, GOOGLE_SATELLITE_SESSION_REQUEST)
  const fetchImpl = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args))
  return new GoogleSatelliteTileLayer(apiKey, store, fetchImpl)
}
